/**
 * Deal Flow Role Utilities
 *
 * Provides role detection and permission helpers for the Deal Flow feature.
 * Supports ADMIN, SUPERADMIN, and EVALUATOR roles.
 *
 * Role Hierarchy:
 * - SUPERADMIN: Full access (rate + approve)
 * - ADMIN: Full access (rate + approve)
 * - EVALUATOR: Limited access (rate only, cannot approve)
 *
 * @see docs/deal_flow_implementation_admin.md
 */

/**
 * Role type constants
 */
export const DEAL_FLOW_ROLES = {
  ADMIN: "ADMIN",
  SUPERADMIN: "SUPERADMIN",
  EVALUATOR: "EVALUATOR",
};

/**
 * Get admin data from localStorage
 * @returns {Object|null} Admin user data or null if not logged in
 */
export const getAdminData = () => {
  try {
    const adminStr = localStorage.getItem("admin");
    if (!adminStr) return null;
    const admin = JSON.parse(adminStr);
    return admin?.data || null;
  } catch (error) {
    console.error("Error parsing admin data:", error);
    return null;
  }
};

/**
 * Get the current user's Deal Flow role
 * @returns {string|null} Role name or null if not determinable
 */
const normalizeRoleName = (roleName) => {
  if (!roleName) return null;
  return roleName
    .toString()
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
};

// Normalized names of the staff seats that are not the platform administrator,
// canonical codes and the legacy catalog's long forms.
const NON_AD_STAFF_ROLE_NAMES = [
  "EMPLOYEE", "BDC", "CONSULTANT", "DR", "CCO", "UDP",
  "DEALING_REPRESENTATIVE", "CHIEF_COMPLIANCE_OFFICER", "ULTIMATE_DESIGNATED_PERSON",
];

export const getDealFlowRole = () => {
  const adminData = getAdminData();
  if (!adminData) return null;

  // Prefer explicit role from AdminRole (or role object) when available
  const rawRoleName =
    adminData.AdminRole?.role ||
    adminData.role?.role ||
    adminData.role?.roles_name ||
    adminData.role_name;

  const roleName = normalizeRoleName(rawRoleName);

  // Canonical held roles decide FIRST: the dark-mode union can name a legacy
  // role (Evaluator, Admin) while the account also holds canonical seats. AD
  // approves; any non-AD staff seat never does, whatever the name says.
  const heldRoles = Array.isArray(adminData.roles)
    ? adminData.roles.map(normalizeRoleName).filter(Boolean)
    : [];
  if (heldRoles.includes("AD")) {
    return DEAL_FLOW_ROLES.ADMIN;
  }
  if (heldRoles.some((role) => NON_AD_STAFF_ROLE_NAMES.includes(role))) {
    return null;
  }

  if (roleName === "EVALUATOR") {
    return DEAL_FLOW_ROLES.EVALUATOR;
  }

  if (roleName === "SUPERADMIN" || roleName === "SUPER_ADMIN") {
    return DEAL_FLOW_ROLES.SUPERADMIN;
  }

  if (roleName === "ADMIN") {
    return DEAL_FLOW_ROLES.ADMIN;
  }

  // The canonical platform administrator. Its login payload names 'AD', which
  // no branch above matched: the AD used to be recognised only by the
  // admin_status fallback below, the same fallback every staff seat matched.
  if (roleName === "AD") {
    return DEAL_FLOW_ROLES.ADMIN;
  }

  // A non-AD staff seat never approves deals: each carries the admin_status
  // shadow, so the fallback below would wrongly make it an ADMIN (WP-H1).
  if (NON_AD_STAFF_ROLE_NAMES.includes(roleName)) {
    return null;
  }

  // Legacy fallback for an admin whose role name is unknown or absent: treat
  // admin_status=1 as ADMIN (not SUPERADMIN).
  if (adminData.admin_status === 1) {
    return DEAL_FLOW_ROLES.ADMIN;
  }

  // Default to null if role cannot be determined
  return null;
};

/**
 * Check if current user can rate campaigns
 * Both Admin and Evaluator roles can rate.
 * @returns {boolean} True if user can rate
 */
export const canRate = () => {
  const role = getDealFlowRole();
  return [
    DEAL_FLOW_ROLES.ADMIN,
    DEAL_FLOW_ROLES.SUPERADMIN,
    DEAL_FLOW_ROLES.EVALUATOR,
  ].includes(role);
};

/**
 * Check if current user can approve/reject campaigns
 * Only Admin and SuperAdmin can approve. Evaluators cannot.
 * @returns {boolean} True if user can approve
 */
export const canApprove = () => {
  const role = getDealFlowRole();
  return [DEAL_FLOW_ROLES.ADMIN, DEAL_FLOW_ROLES.SUPERADMIN].includes(role);
};

/**
 * Check if current user is an Evaluator (can rate but not approve)
 * @returns {boolean} True if user is an Evaluator
 */
export const isEvaluator = () => {
  return getDealFlowRole() === DEAL_FLOW_ROLES.EVALUATOR;
};

/**
 * Check if current user is an Admin or SuperAdmin
 * @returns {boolean} True if user is Admin or SuperAdmin
 */
export const isAdmin = () => {
  const role = getDealFlowRole();
  return [DEAL_FLOW_ROLES.ADMIN, DEAL_FLOW_ROLES.SUPERADMIN].includes(role);
};

/**
 * Get the rater type for API calls
 * This matches the backend's expected rater_type values.
 * @returns {string} Rater type: 'ADMIN' or 'EVALUATOR'
 */
export const getRaterType = () => {
  const role = getDealFlowRole();

  if (role === DEAL_FLOW_ROLES.EVALUATOR) {
    return "EVALUATOR";
  }

  // Admin and SuperAdmin both submit as ADMIN type
  return "ADMIN";
};

/**
 * Hook-like function to get all Deal Flow role info at once
 * Useful for components that need multiple permission checks
 * @returns {Object} Object with role info and permission flags
 */
export const getDealFlowPermissions = () => {
  const role = getDealFlowRole();
  const adminData = getAdminData();

  return {
    role,
    adminData,
    canRate: canRate(),
    canApprove: canApprove(),
    isEvaluator: isEvaluator(),
    isAdmin: isAdmin(),
    raterType: getRaterType(),
    userName: adminData
      ? `${adminData.firstname || ""} ${adminData.lastname || ""}`.trim()
      : "Unknown",
  };
};

export default {
  DEAL_FLOW_ROLES,
  getAdminData,
  getDealFlowRole,
  canRate,
  canApprove,
  isEvaluator,
  isAdmin,
  getRaterType,
  getDealFlowPermissions,
};
